import {createApp} from 'vue'
import App from './App.vue'
import Login from './Login.vue'
import axios from 'ts-axios-new'
import {createRouter, createWebHashHistory} from 'vue-router'
import ElementPlus from 'element-plus'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import {update} from './libs/utils'
import {initCountry, initProduct, initUser, initUserDirectory, initUAChannel} from './libs/init'
import {getLocalObj, setLocalObj} from "./libs/storage"
import VueGridLayout from 'vue-grid-layout'
import 'element-plus/dist/index.css'
import 'element-plus/theme-chalk/dark/css-vars.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'

import {routes} from './routes';

let login: any;
let authRequestInterceptorInstalled = false;
declare const google: any;

function mountLoginFallback() {
    if (login) {
        return;
    }
    login = createApp(Login);
    login.mount('#login');
}

function waitForGoogle(tries = 0): Promise<typeof google> {
    if (typeof google !== 'undefined' && google?.accounts?.id) {
        return Promise.resolve(google);
    }
    if (tries > 200) {
        return Promise.reject(new Error('GSI 脚本加载超时'));
    }
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            waitForGoogle(tries + 1).then(resolve, reject);
        }, 30);
    });
}

function installAuthRequestInterceptor(token: string) {
    if (authRequestInterceptorInstalled) {
        return;
    }
    authRequestInterceptorInstalled = true;
    axios.interceptors.request.use(
        (config) => {
            if (token) {
                config.headers['Authorization'] = token;
            }
            return config;
        },
        (err) => Promise.reject(err)
    );
}

export const root_props = {
    // user info
    user: {
        user_id: '',
        avatar: '',
        name: '',
        email: '',
        authority: [],
        product: [],
    },
    // auth scope permission
    country_list: [],
    product_list: [],
    ua_channel: [],
    user_list: [],
};

const router = createRouter({
    history: createWebHashHistory(),
    routes
});

router.beforeEach((to, from, next) => {
    let title: string = '统计分析平台';
    to.matched.forEach(m => {
        if (m.meta.title) {
            title = m.meta.title;
        }
    });
    document.title = title;
    const roles = root_props.user.authority || [];
    const productId = typeof to.params.productId === 'string' ? to.params.productId : '';
    const hasScopedProductRole = roles.some(role => ['product', 'ua', 'iaa'].includes(role));
    const canAccessProduct = roles.includes('pm')
        || hasScopedProductRole && (root_props.user.product || []).includes(productId);
    if (productId && !canAccessProduct) {
        next({name: 'Index'});
        return;
    }
    if (to.name === 'SupervisorAuthority' && !roles.includes('manager')) {
        next({name: 'Index'});
        return;
    }
    if (to.name === 'SupervisorDashboard'
        && !roles.some(role => ['manager', 'pm', 'ua', 'iaa', 'black'].includes(role))) {
        next({name: 'Index'});
        return;
    }
    next();
});

function init() {
    axios.all([initCountry(function (res) {
        update(root_props.country_list, res.data.data.countryList);
    }), initProduct(function (res) {
        // The API already returns only products visible to the current user.
        update(root_props.product_list, res.data.data.productList);
    }), (root_props.user.authority.includes('manager') ? initUser : initUserDirectory)(function (res) {
        update(root_props.user_list, res.data.data.userList);
    }), initUAChannel(function (res) {
        update(root_props.ua_channel, res.data.data.channelList);
    })]).then(function () {
        const bookmark = getLocalObj('bookmark', []);
        root_props.product_list.forEach(p => {
            p.bookmark = false;
            bookmark.forEach(b => {
                if (b === p.id) {
                    p.bookmark = true;
                }
            })
        });
        root_props.product_list.sort(function (a, b) {
            if (a.bookmark ^ b.bookmark) {
                return a.bookmark ? -1 : 1;
            } else {
                return a.id > b.id ? -1 : 1;
            }
        });
        const app = createApp(App);
        app.use(router);
        app.use(VueGridLayout);
        app.use(ElementPlus, {locale: zhCn});
        app.config.unwrapInjectedRef = true;
        for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
            app.component(key, component)
        }
        const vm = app.mount('#app');
        update(vm, root_props);
    })
}

window.onload = function () {
    const authorization = getLocalObj('authorization', null);
    const meHeaders: Record<string, string> = {};
    if (authorization) {
        meHeaders['Authorization'] = authorization;
    }
    axios
        .get('/api/v1/me', {headers: meHeaders})
        .then((res) => {
            update(root_props.user, res.data.data);
            installAuthRequestInterceptor(authorization);
            init();
        })
        .catch(() => {
            try {
                localStorage.removeItem('authorization');
            } catch (e) {
                /* empty */
            }
            waitForGoogle()
                .then((g) => {
                    g.accounts.id.initialize({
                        client_id: import.meta.env.VITE_OAUTH2_CLIENT_ID,
                        callback: (response: any) => {
                            axios
                                .post('/api/v1/oauth2/login', {
                                    platform: 'google',
                                    id_token: response.credential,
                                })
                                .then((res) => {
                                    update(root_props.user, res.data.data);
                                    setLocalObj('authorization', res.data.data.access_token);
                                    installAuthRequestInterceptor(res.data.data.access_token);
                                    if (login) {
                                        login.unmount();
                                    }
                                    init();
                                })
                                .catch(() => {
                                    mountLoginFallback();
                                });
                        },
                        auto_select: true,
                    });
                    // 不依赖 One Tap 的 prompt 回调；One Tap 常被浏览器/策略拦截，主入口改为始终提供「使用 Google 登录」按钮
                    mountLoginFallback();
                    g.accounts.id.prompt();
                })
                .catch((e) => {
                    console.error(e);
                    const el = document.getElementById('login');
                    if (el) {
                        el.textContent = '无法加载 Google 登录组件，请检查网络后刷新页面。';
                    }
                });
        });
};
